import React from 'react';
import { Button } from '../components/ui/button';
import { CheckCircle, HelpCircle, ArrowRight } from 'lucide-react';
import { mockPricing } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Pricing = () => {
  const pricingFAQs = [
    {
      question: "What's included in the membership?",
      answer: "The membership includes unlimited virtual visits for you and up to 5 family members, 24/7 provider access, no per-visit fees, and direct prescription delivery to your pharmacy when medically appropriate."
    },
    {
      question: "How does insurance billing work?",
      answer: "We bill your insurance directly. You're responsible for your standard copay as determined by your insurance plan. We accept most major insurance providers and will verify your coverage before your visit."
    },
    {
      question: "Can I use FSA/HSA funds?",
      answer: "Yes! Both membership fees and pay-per-visit charges are eligible for FSA and HSA reimbursement. Save your receipt for reimbursement purposes."
    },
    {
      question: "Are there any hidden fees?",
      answer: "No hidden fees. The price you see is the price you pay. Lab work, imaging, or specialist referrals may incur additional costs, but we'll always inform you upfront."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
            Transparent Pricing
          </h1>
          <p className="text-xl text-[#5F6C7B] max-w-3xl mx-auto">
            Choose the payment option that works best for you. No surprises, no hidden fees.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {mockPricing.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-2xl p-8 border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.highlighted
                    ? 'border-[#0F4C81] bg-gradient-to-br from-[#F4F6F8] to-white shadow-lg transform scale-105'
                    : 'border-gray-200 bg-white hover:border-[#1FB6B2]'
                }`}
              >
                {plan.highlighted && (
                  <div className="inline-block px-4 py-1 bg-[#0F4C81] text-white text-sm font-semibold rounded-full mb-4">
                    Best Value
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#12324A] mb-2">{plan.title}</h3>
                <p className="text-[#5F6C7B] mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-[#0F4C81]">{plan.price}</span>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="text-[#1FB6B2] flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-[#5F6C7B]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full py-6 text-lg font-semibold rounded-xl transition-all duration-200 ${
                    plan.highlighted
                      ? 'bg-[#0F4C81] hover:bg-[#12324A] text-white shadow-lg'
                      : 'bg-white border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Compare Options
            </h2>
            <p className="text-xl text-[#5F6C7B]">
              Find the right plan for your healthcare needs
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#F4F6F8]">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-[#12324A]">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#12324A]">Insurance/Medicaid</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#12324A]">Membership</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-[#12324A]">Pay Per Visit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">Virtual visits</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">24/7 provider access</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">Prescriptions</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">Unlimited visits</td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">Per copay</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">-</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">Family coverage (up to 5)</td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">Varies</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">-</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-[#5F6C7B]">No per-visit fees</td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">-</td>
                    <td className="px-6 py-4 text-center"><CheckCircle className="text-[#1FB6B2] mx-auto" size={20} /></td>
                    <td className="px-6 py-4 text-center text-sm text-[#5F6C7B]">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Pricing Questions
            </h2>
            <p className="text-xl text-[#5F6C7B]">
              Common questions about our pricing options
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {pricingFAQs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-gray-200 rounded-xl px-6 bg-white hover:border-[#1FB6B2] transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-[#12324A] hover:text-[#0F4C81] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5F6C7B] pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-start space-x-3">
              <HelpCircle className="text-[#5F6C7B] flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="text-sm text-[#5F6C7B] leading-relaxed">
                  <strong>Important:</strong> Coverage and copays vary by plan and state. Medicaid and insurance availability depends on enrollment and network status. Providers may prescribe medications when medically appropriate and permitted by law. Individual results vary. TouchScreenMD does not guarantee specific outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Choose your preferred payment option and connect with a provider today.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
          >
            Start a Visit Now
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
